import { useSearchParams } from 'react-router-dom'
import { useCallback, useMemo } from 'react'

export interface UrlState {
  severity: string[]
  searches: string[]  // Multiple search terms as pills
  chartType: 'bar' | 'area'
  timeRange: '7d' | '30d' | '90d' | '12m' | 'all'
  timeBucket: string | null  // Selected time bucket for drill-down (e.g., "2024-01")
  timeBucketLabel: string | null  // Display label for the bucket (e.g., "Jan '24")
  excludeManual: boolean
  excludeAi: boolean
}

const DEFAULTS: UrlState = {
  severity: [],
  searches: [],
  chartType: 'bar',
  timeRange: 'all',
  timeBucket: null,
  timeBucketLabel: null,
  excludeManual: false,
  excludeAi: false,
}

/**
 * Syncs dashboard state with URL query params for shareable links.
 *
 * Example URL:
 * ?search=oracle&chart=area&range=30d&excludeManual=1
 */
export function useUrlState() {
  const [searchParams, setSearchParams] = useSearchParams()

  const state = useMemo((): UrlState => {
    const severityParam = searchParams.get('severity')
    const searchesParam = searchParams.get('searches')
    const chartParam = searchParams.get('chart')
    const timeRangeParam = searchParams.get('range')
    const timeBucketParam = searchParams.get('bucket')
    const timeBucketLabelParam = searchParams.get('bucketLabel')

    return {
      severity: severityParam ? severityParam.split(',').filter(Boolean) : DEFAULTS.severity,
      searches: searchesParam ? searchesParam.split(',').filter(Boolean) : DEFAULTS.searches,
      chartType: chartParam === 'area' ? 'area' : DEFAULTS.chartType,
      timeRange: (['7d', '30d', '90d', '12m', 'all'].includes(timeRangeParam || '')
        ? timeRangeParam as UrlState['timeRange']
        : DEFAULTS.timeRange),
      timeBucket: timeBucketParam || DEFAULTS.timeBucket,
      timeBucketLabel: timeBucketLabelParam ? decodeURIComponent(timeBucketLabelParam) : DEFAULTS.timeBucketLabel,
      excludeManual: searchParams.get('excludeManual') === '1',
      excludeAi: searchParams.get('excludeAi') === '1',
    }
  }, [searchParams])

  const setState = useCallback((updates: Partial<UrlState> | ((prev: UrlState) => Partial<UrlState>)) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      const resolvedUpdates = typeof updates === 'function' ? updates(state) : updates

      if ('severity' in resolvedUpdates) {
        if (resolvedUpdates.severity && resolvedUpdates.severity.length > 0) {
          newParams.set('severity', resolvedUpdates.severity.join(','))
        } else {
          newParams.delete('severity')
        }
      }

      if ('searches' in resolvedUpdates) {
        if (resolvedUpdates.searches && resolvedUpdates.searches.length > 0) {
          newParams.set('searches', resolvedUpdates.searches.join(','))
        } else {
          newParams.delete('searches')
        }
      }

      if ('chartType' in resolvedUpdates) {
        if (resolvedUpdates.chartType && resolvedUpdates.chartType !== DEFAULTS.chartType) {
          newParams.set('chart', resolvedUpdates.chartType)
        } else {
          newParams.delete('chart')
        }
      }

      if ('timeRange' in resolvedUpdates) {
        if (resolvedUpdates.timeRange && resolvedUpdates.timeRange !== DEFAULTS.timeRange) {
          newParams.set('range', resolvedUpdates.timeRange)
        } else {
          newParams.delete('range')
        }
      }

      if ('timeBucket' in resolvedUpdates) {
        if (resolvedUpdates.timeBucket) {
          newParams.set('bucket', resolvedUpdates.timeBucket)
        } else {
          newParams.delete('bucket')
        }
      }

      if ('timeBucketLabel' in resolvedUpdates) {
        if (resolvedUpdates.timeBucketLabel) {
          newParams.set('bucketLabel', encodeURIComponent(resolvedUpdates.timeBucketLabel))
        } else {
          newParams.delete('bucketLabel')
        }
      }

      if ('excludeManual' in resolvedUpdates) {
        if (resolvedUpdates.excludeManual) {
          newParams.set('excludeManual', '1')
        } else {
          newParams.delete('excludeManual')
        }
      }

      if ('excludeAi' in resolvedUpdates) {
        if (resolvedUpdates.excludeAi) {
          newParams.set('excludeAi', '1')
        } else {
          newParams.delete('excludeAi')
        }
      }

      return newParams
    }, { replace: true })
  }, [setSearchParams, state])

  const clearAll = useCallback(() => {
    setSearchParams({}, { replace: true })
  }, [setSearchParams])

  return { state, setState, clearAll }
}
